import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { socketService } from '../../utils/socketService';
import {
  FiFlag, FiAlertTriangle, FiCheckCircle, FiXCircle,
  FiSearch, FiFilter, FiEye, FiMessageSquare,
  FiPackage, FiUser, FiClock, FiShield, FiRefreshCw,
} from 'react-icons/fi';

function AdminModeration() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    const handler = () => loadReports();
    socketService.connect().catch(() => {});
    const unsub = socketService.on('content_report', handler);
    const unsubNotif = socketService.on('notification', (n) => {
      if (n?.type === 'content_report') handler();
    });
    return () => {
      unsub?.();
      unsubNotif?.();
    };
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await api.getContentReports();
      if (response && response.success) {
        setReports(response.data || []);
      } else {
        console.error('Erreur lors du chargement des signalements:', response?.message);
        setReports([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des signalements:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveReport = async (reportId, action) => {
    try {
      const response = await api.resolveReport(reportId, action);
      if (response && response.success) {
        alert(`Signalement ${action === 'approve' ? 'approuvé' : 'rejeté'} avec succès`);
        loadReports();
        setSelectedReport(null);
      } else {
        throw new Error(response?.message || 'Erreur lors de la résolution');
      }
    } catch (error) {
      console.error('Erreur lors de la résolution:', error);
      alert('Erreur lors de la résolution du signalement: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'MESSAGE':
        return <FiMessageSquare className="w-5 h-5" />;
      case 'QUOTE':
        return <FiPackage className="w-5 h-5" />;
      case 'USER':
        return <FiUser className="w-5 h-5" />;
      default:
        return <FiFlag className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      MESSAGE: 'Message',
      USER: 'Profil',
      QUOTE: 'Devis / mission',
    };
    return labels[type] || type;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500/20 border-red-500/30 text-red-400';
      case 'medium':
        return 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400';
      case 'low':
        return 'bg-blue-500/20 border-blue-500/30 text-blue-400';
      default:
        return 'bg-gray-500/20 border-gray-500/30 text-gray-400';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400';
      case 'resolved':
        return 'bg-green-500/20 border-green-500/30 text-green-400';
      case 'dismissed':
        return 'bg-gray-500/20 border-gray-500/30 text-gray-400';
      default:
        return 'bg-gray-500/20 border-gray-500/30 text-gray-400';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReports = reports.filter(report => {
    if (filter !== 'all' && report.status !== filter) return false;
    if (searchQuery && !report.description.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !report.reason.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const pendingReports = reports.filter(r => r.status === 'pending');
  const resolvedReports = reports.filter(r => r.status === 'resolved');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">Modération de contenu</h1>
          <p className="text-gray-400">
            Signalements envoyés depuis l&apos;app. Approuver masque le message ; Rejeter classe sans action.
          </p>
        </div>
        <button
          type="button"
          onClick={loadReports}
          disabled={loading}
          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg flex items-center disabled:opacity-50"
        >
          <FiRefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Total signalements</p>
            <FiFlag className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{reports.length}</p>
        </div>
        <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">En attente</p>
            <FiClock className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-yellow-400">{pendingReports.length}</p>
        </div>
        <div className="bg-gray-800 border border-green-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Résolus</p>
            <FiCheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-green-400">{resolvedReports.length}</p>
        </div>
        <div className="bg-gray-800 border border-red-600/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-400">Priorité élevée</p>
            <FiAlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-3xl font-bold text-red-400">
            {reports.filter(r => r.priority === 'high').length}
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher dans les signalements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="resolved">Résolus</option>
            <option value="dismissed">Rejetés</option>
          </select>
        </div>
      </div>

      {/* Liste des signalements */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Chargement des signalements...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="p-12 text-center">
            <FiShield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Aucun signalement</p>
            <p className="text-gray-500 text-sm mt-2">Tout semble propre !</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {filteredReports.map((report) => (
              <div
                key={report.id}
                className="p-6 hover:bg-gray-700/30 transition-colors cursor-pointer"
                onClick={() => setSelectedReport(report)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`p-3 rounded-lg border ${getPriorityColor(report.priority)}`}>
                      {getTypeIcon(report.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-bold text-white">{report.reason}</h3>
                        <span className={`px-2 py-1 text-xs rounded border ${getStatusColor(report.status)}`}>
                          {report.status === 'pending' ? 'En attente' : report.status === 'resolved' ? 'Résolu' : 'Rejeté'}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded border ${getPriorityColor(report.priority)}`}>
                          {report.priority === 'high' ? 'Élevé' : report.priority === 'medium' ? 'Moyen' : 'Faible'}
                        </span>
                        <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded">
                          {getTypeLabel(report.type)}
                        </span>
                      </div>
                      {report.targetContent?.preview && report.targetContent.preview !== 'N/A' && (
                        <p className="text-gray-400 text-sm mb-2 italic border-l-2 border-gray-600 pl-3">
                          « {report.targetContent.preview} »
                        </p>
                      )}
                      {report.description && (
                        <p className="text-gray-300 mb-3">{report.description}</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400 mb-1">Signalé par:</p>
                          <p className="text-white font-semibold">{report.reportedBy.name}</p>
                          <p className="text-gray-400 text-xs">{report.reportedBy.email}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 mb-1">Utilisateur concerné:</p>
                          <p className="text-white font-semibold">{report.targetUser.name}</p>
                          <p className="text-gray-400 text-xs">{report.targetUser.email}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-4">
                        Signalé le {formatDate(report.createdAt)}
                      </p>
                    </div>
                  </div>
                  {report.status === 'pending' && (
                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResolveReport(report.id, 'approve');
                        }}
                        className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors flex items-center text-sm"
                      >
                        <FiCheckCircle className="w-4 h-4 mr-2" />
                        Approuver
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResolveReport(report.id, 'dismiss');
                        }}
                        className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors flex items-center text-sm"
                      >
                        <FiXCircle className="w-4 h-4 mr-2" />
                        Rejeter
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReport(report);
                        }}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center text-sm"
                      >
                        <FiEye className="w-4 h-4 mr-2" />
                        Voir détails
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Détails du signalement</h2>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-400 hover:text-white"
              >
                <FiXCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Raison</p>
                <p className="text-white font-semibold text-lg">{selectedReport.reason}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-400 mb-1">Description</p>
                <p className="text-white">{selectedReport.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Type</p>
                  <p className="text-white">{selectedReport.type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Priorité</p>
                  <span className={`px-3 py-1 text-xs rounded border ${getPriorityColor(selectedReport.priority)}`}>
                    {selectedReport.priority}
                  </span>
                </div>
              </div>
              
              <div className="border-t border-gray-700 pt-4">
                <p className="text-sm font-semibold text-gray-400 mb-3">Signalé par</p>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-white font-semibold">{selectedReport.reportedBy.name}</p>
                  <p className="text-gray-400 text-sm">{selectedReport.reportedBy.email}</p>
                </div>
              </div>
              
              <div className="border-t border-gray-700 pt-4">
                <p className="text-sm font-semibold text-gray-400 mb-3">Utilisateur concerné</p>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-white font-semibold">{selectedReport.targetUser.name}</p>
                  <p className="text-gray-400 text-sm">{selectedReport.targetUser.email}</p>
                </div>
              </div>
              
              {selectedReport.status === 'pending' && (
                <div className="flex space-x-4 pt-4 border-t border-gray-700">
                  <button
                    onClick={() => {
                      handleResolveReport(selectedReport.id, 'approve');
                    }}
                    className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors flex items-center justify-center"
                  >
                    <FiCheckCircle className="w-5 h-5 mr-2" />
                    Approuver et prendre action
                  </button>
                  <button
                    onClick={() => {
                      handleResolveReport(selectedReport.id, 'dismiss');
                    }}
                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors flex items-center justify-center"
                  >
                    <FiXCircle className="w-5 h-5 mr-2" />
                    Rejeter le signalement
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminModeration;
